import React, { useCallback, useEffect, useRef, useState } from "react";
import * as R from "ramda";
import SignatureCanvas from "react-signature-canvas";
import CSSModules from "react-css-modules";
import css from "./styles.scss";
import { noop } from "lodash";
import { Div } from "components/Base";
import InputTypeSwitcher from "./InputTypeSwitcher/InputTypeSwitcher";
import SignatureImage from "./SignatureImage/SignatureImage";
import SignatureType from "./SignatureType/SignatureType";
import ClearSignatureButton from "./ClearSignatureButton/ClearSignatureButton";
import { debounce } from "lodash";

const EDITOR_CONTAINER_STYLE = {
  width: 300,
  height: 100,
  position: "relative",
  background: "white"
};

const DRAWING_CONTAINER_STYLE = {
  position: "absolute",
  top: 0,
  left: 0,
  zIndex: 1
};

const SIGNATURE_LINE_STYLE = {
  width: "270px",
  height: "1px",
  background: "#c9d7f6",
  position: "absolute",
  bottom: "15px",
  left: "15px",
  zIndex: 0
};

const CANVAS_BASE_PROPS = { width: "270px", height: "100px" };

const Signature = ({ onEnd = noop, onChange = noop, value, disabled }) => {
  const { value: base64Value = "" } = value || {};
  const [isUsingDraw, setIsUsingDraw] = useState(true);
  const [typedSignature, setTypedSignature] = useState("");
  const typingRef = useRef(null);
  const drawingRef = useRef();
  const readOnly = disabled && base64Value?.length;

  const clearCanvas = useCallback(() => {
    void drawingRef.current?.clear();
  }, [drawingRef.current]);

  const clearSignature = useCallback(() => {
    onChange("");
    onEnd("");
    if (isUsingDraw) {
      clearCanvas();
    } else {
      setTypedSignature("");
    }
  }, [onChange, onEnd, clearCanvas, setTypedSignature]);

  const switchToDraw = useCallback(() => {
    setIsUsingDraw(true);
    setTypedSignature("");
  }, [setIsUsingDraw]);

  const switchToType = useCallback(() => {
    setIsUsingDraw(false);
  }, [setIsUsingDraw]);

  useEffect(() => {
    if (!readOnly && !R.isEmpty(base64Value) && isUsingDraw) {
      void drawingRef?.current?.fromDataURL(base64Value);
    }
  }, []);

  useEffect(() => {
    if (!readOnly && isUsingDraw) {
      if (!disabled) {
        void drawingRef.current?.on();
      } else {
        void drawingRef.current?.off();
      }
    }
  }, [disabled]);

  useEffect(() => {
    const adjustFontSize = () => {
      const inputElement = typingRef.current;
      if (!inputElement) return;
      const parentElement = inputElement.parentElement;
      const originalFontSize = window.getComputedStyle(inputElement).fontSize;
      const originalWidth = inputElement.offsetWidth;

      let fontSize = originalFontSize;

      while (
        inputElement.scrollWidth > originalWidth &&
        parseFloat(fontSize) > 10
      ) {
        fontSize = parseFloat(fontSize) - 1;
        inputElement.style.fontSize = `${fontSize}px`;
      }

      parentElement.style.fontSize = `${fontSize}px`;

      if (
        inputElement.scrollWidth <= originalWidth &&
        parseFloat(fontSize) < 20
      ) {
        while (
          inputElement.scrollWidth <= originalWidth &&
          parseFloat(fontSize) < 20
        ) {
          fontSize = parseFloat(fontSize) + 1;
          inputElement.style.fontSize = `${fontSize}px`;
        }
        parentElement.style.fontSize = `${fontSize}px`;
      }
    };

    adjustFontSize();

    window.addEventListener("resize", adjustFontSize);

    return () => {
      window.removeEventListener("resize", adjustFontSize);
    };
  }, [typedSignature]);

  const handleTyping = text => {
    if (
      typingRef.current.style.fontSize === "10px" &&
      text.length > typedSignature.length
    ) {
      return;
    }
    setTypedSignature(text);
    if (text === "") {
      typingRef.current.style.fontSize = "25px";
      typingRef.current.parentElement.style.fontSize = "25px";
    }
  };

  const onInputEnd = () => {
    let imgBase64;
    if (isUsingDraw) {
      imgBase64 = drawingRef.current?.toDataURL();
    } else {
      const input = typingRef.current;
      const text = typedSignature;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = 300;
      canvas.height = 100;
      context.font = window.getComputedStyle(input).font;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillStyle = window.getComputedStyle(input).color;
      const x = canvas.width / 2;
      const y = canvas.height / 2;
      context.fillText(text, x, y);
      imgBase64 = canvas.toDataURL("image/png");
    }
    onChange({ value: imgBase64, type: "signature" });
    onEnd(imgBase64);
  };

  return (
    <Div>
      {!disabled && (
        <InputTypeSwitcher
          switchToType={switchToType}
          switchToDraw={switchToDraw}
          isUsingDraw={isUsingDraw}
        />
      )}
      <Div
        style={{
          ...EDITOR_CONTAINER_STYLE,
          border: `solid 1px #${!disabled ? "333" : "d4d4d4"}`
        }}
      >
        {readOnly ? (
          <SignatureImage base64Value={base64Value} />
        ) : !isUsingDraw ? (
          <SignatureType
            readOnly={disabled}
            onTypeEnd={debounce(() => onInputEnd(), 300)}
            typingRef={typingRef}
            handleTyping={handleTyping}
            typedSignature={typedSignature}
          />
        ) : (
          <>
            <Div style={DRAWING_CONTAINER_STYLE}>
              <SignatureCanvas
                ref={drawingRef}
                onEnd={onInputEnd}
                dotSize={1}
                throttle={16}
                canvasProps={{
                  ...CANVAS_BASE_PROPS,
                  display:
                    disabled && base64Value && base64Value.length
                      ? "none"
                      : undefined
                }}
              />
            </Div>
            <Div
              style={{
                ...SIGNATURE_LINE_STYLE,
                ...{ opacity: disabled ? 0 : 1 }
              }}
            ></Div>
          </>
        )}
        {!disabled && <ClearSignatureButton onClick={clearSignature} />}
      </Div>
    </Div>
  );
};

export default CSSModules(css)(Signature);
